import { Container } from "../di/container.js";
import { isSingleton } from "../di/decorators.js";
import { getGlobalProviders } from "./global.js";
import { callOnApplicationBootstrap, callOnModuleInit } from "./hooks.js";
import { ModuleRefImpl } from "./module-ref.js";
import { ExtendHelper } from "./extend-helper.js";
class Application {
    constructor(options = {
        containerOptions: {
            autoRegister: true
        }
    }){
        this.modules = new Map();
        this.moduleRefs = new Map();
        this.moduleInstances = [];
        this.classToModuleMap = new Map();
        this.isBootstrapped = false;
        this.singletonAsDefault = true;
        this.container = options.container || new Container(options.containerOptions);
        this.singletonAsDefault = options.singletonAsDefault ?? true;
        this.setupModuleRefFactory();
    }
    setupModuleRefFactory() {
        this.container.registerFactory(ModuleRefImpl, ()=>{
            throw new Error('ModuleRef should be injected directly, not resolved from container');
        }, [], false);
    }
    static create(rootModule, options) {
        const app = new Application(options);
        app.rootModule = rootModule;
        return app;
    }
    async bootstrap() {
        if (this.isBootstrapped) throw new Error('Application has already been bootstrapped');
        if (!this.rootModule) throw new Error('Root module is required for bootstrapping');
        await this.registerGlobalProviders();
        await this.registerModule(this.rootModule);
        await this.callApplicationBootstrapHooks();
        this.isBootstrapped = true;
    }
    async registerGlobalProviders() {
        const globalProviders = getGlobalProviders();
        for (const provider of globalProviders)this.container.register({
            type: 'type',
            token: provider,
            useClass: provider,
            singleton: this.singletonAsDefault || isSingleton(provider)
        });
    }
    async registerModule(moduleClass) {
        if (this.modules.has(moduleClass)) return;
        const metadata = this.getModuleMetadata(moduleClass);
        if (!metadata) throw new Error(`Module ${moduleClass.name} must be decorated with @Module`);
        if (metadata.imports) for (const importedModule of metadata.imports)await this.registerModule(importedModule);
        const extendedModuleClass = this.applyExtensionsIfAny(moduleClass);
        this.container.register({
            type: 'type',
            token: moduleClass,
            useClass: extendedModuleClass,
            singleton: false
        });
        if (metadata.providers) for (const provider of metadata.providers){
            this.classToModuleMap.set(provider, moduleClass);
            const extendedProvider = this.applyExtensionsIfAny(provider);
            this.container.register({
                type: 'type',
                token: provider,
                useClass: extendedProvider,
                singleton: this.singletonAsDefault || isSingleton(provider)
            });
        }
        if (metadata.controllers) for (const controller of metadata.controllers){
            this.classToModuleMap.set(controller, moduleClass);
            const extendedController = this.applyExtensionsIfAny(controller);
            this.container.register({
                type: 'type',
                token: controller,
                useClass: extendedController,
                singleton: isSingleton(controller)
            });
        }
        this.classToModuleMap.set(moduleClass, moduleClass);
        const moduleRef = new ModuleRefImpl(moduleClass, metadata, this.container);
        moduleRef._setModuleInstancesRegistry(this.modules);
        this.moduleRefs.set(moduleClass, moduleRef);
        if (metadata.imports) for (const importedModule of metadata.imports){
            const importedModuleRef = this.moduleRefs.get(importedModule);
            if (importedModuleRef) {
                moduleRef._setImportedModuleRef(importedModule, importedModuleRef);
                importedModuleRef._setModuleInstancesRegistry(this.modules);
            }
        }
        const moduleInstance = this.createModuleWithCorrectRef(moduleClass, moduleRef);
        this.modules.set(moduleClass, moduleInstance);
        await callOnModuleInit(moduleInstance);
        this.moduleInstances.push(moduleInstance);
        const instancePromises = [];
        if (metadata.providers) instancePromises.push(...metadata.providers.map(async (provider)=>{
            const instance = await this.container.resolve(provider);
            await callOnModuleInit(instance);
            return instance;
        }));
        if (metadata.controllers) instancePromises.push(...metadata.controllers.map(async (controller)=>{
            const instance = await this.container.resolve(controller);
            await callOnModuleInit(instance);
            return instance;
        }));
        const instances = await Promise.all(instancePromises);
        this.moduleInstances.push(...instances);
    }
    createModuleWithCorrectRef(moduleClass, moduleRef) {
        try {
            const ModuleConstructor = moduleClass;
            return new ModuleConstructor(moduleRef);
        } catch (error) {
            this.container.registerValue(ModuleRefImpl, moduleRef);
            const moduleInstance = this.container.get(moduleClass);
            return moduleInstance;
        }
    }
    getModuleMetadata(moduleClass) {
        return moduleClass.prototype?.metadata;
    }
    applyExtensionsIfAny(originalClass) {
        const extendHelper = ExtendHelper.getInstance();
        return extendHelper.applyExtensions(originalClass);
    }
    get(token) {
        if (!this.isBootstrapped) throw new Error('Application must be bootstrapped before resolving dependencies');
        if (this.modules.has(token)) return this.modules.get(token);
        return this.container.get(token);
    }
    getContainer() {
        return this.container;
    }
    getModuleRef(moduleClass) {
        return this.moduleRefs.get(moduleClass);
    }
    getAllModuleRefs() {
        return Array.from(this.moduleRefs.values());
    }
    async callApplicationBootstrapHooks() {
        for (const instance of this.moduleInstances)await callOnApplicationBootstrap(instance);
    }
    async close() {
        this.modules.clear();
        this.moduleRefs.clear();
        this.moduleInstances.length = 0;
        this.isBootstrapped = false;
    }
}
export { Application };
